import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { List } from "lucide-react";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { authService } from "../../services/auth";
import { useInsightCarousel } from "../../hooks/useInsightCarousel";
import { usePlayList } from "../../hooks/usePlayList";
import InsightCard from "./InsightCard";
import ComingSoonModal from "./ComingSoonModal";

// Import existing Pista theme styles
import "../../styles/pista-theme.css";
import logger from '../../utils/logger';

const DISMISSED_KEY = 'pista_dismissed_insights';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function loadDismissed() {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveDismissed(map) {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(map));
  } catch {
    // silent – localStorage may be full
  }
}

function filterDismissed(insights) {
  const map = loadDismissed();
  const now = Date.now();
  return insights.filter((insight) => {
    const label = insight.label;
    if (!label || !map[label]) return true;
    return now - map[label] >= DISMISS_TTL_MS;
  });
}

/**
 * Insight Carousel Component
 * Replaces RecentActivityCard with a revolving carousel of insight cards
 * Each card has insight text and CTA buttons (Keep going / Switch, or single CTA for explore)
 *
 * @param {Object} props
 * @param {boolean} props.compact - Whether to show compact version
 * @param {number} props.autoRotateInterval - Auto-rotation interval in ms
 * @param {boolean} props.showNavigation - Whether to show dot navigation
 * @param {Function} props.onOpenCurate - Callback to open Curate sidebar (passed from parent)
 * @param {string} props.className - Additional CSS class
 * @param {string} props.view - Filter insights by context: 'sessions', 'collection', or omit for all (recent-activity only)
 * @param {string} props.variant - Set to 'explore' for /api/explore/insights (anonymous or auth)
 * @param {Function} props.onSearchSessions - (query: string) => void  — PastSessions search pre-fill
 * @param {Function} props.onFilterWins - () => void — PastSessions wins-only toggle
 * @param {Function} props.onAddToPlayList - (game: { game_id, name }) => void — add to shared Play List state
 * @param {Function} props.onCollectionAction - ({ type, sortBy?, sortOrder? }) => void — Collection filter/sort
 * @param {Function} props.onLetsPlay - (game: {id, name}) => void — open LetsPlay modal for a game
 * @param {Function} props.onShowSimilar - (game: {id, name}) => void — open Curate sidebar for similarity
 * @param {Object|null} props.user - Current user object
 */
function InsightCarousel({
  compact = true,
  autoRotateInterval = 5000,
  showNavigation = true,
  onOpenCurate,
  onSearchSessions,
  onFilterWins,
  onAddToPlayList,
  onCollectionAction,
  onLetsPlay,
  onShowSimilar,
  className = "",
  view,
  variant,
  user,
}) {
  const navigate = useNavigate();
  const { addToPlayList } = usePlayList(user);
  const [rawInsights, setRawInsights] = useState([]);
  const [dismissTick, setDismissTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [comingSoonMessage, setComingSoonMessage] = useState(null);
  const [playListBanner, setPlayListBanner] = useState({ show: false, message: null });

  const showPlayListBanner = useCallback((message) => {
    setPlayListBanner({ show: true, message });
    setTimeout(() => setPlayListBanner({ show: false, message: null }), 3000);
  }, []);

  // Re-evaluate dismissed filter whenever rawInsights changes or a dismissal occurs (dismissTick)
  const insights = useMemo(() => filterDismissed(rawInsights), [rawInsights, dismissTick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        setLoading(true);

        if (variant === "explore") {
          const response = await httpRequest(`${API_BASE}/api/explore/insights`, {
            method: "GET",
            headers: {
              ...authService.getAuthHeaders(),
            },
          });
          if (response.ok) {
            const responseData = await response.json();
            if (responseData?.insights?.length > 0) {
              setRawInsights(responseData.insights);
            } else {
              setRawInsights([]);
            }
          } else {
            setRawInsights([]);
          }
          return;
        }

        const token = authService.getToken();
        if (!token) {
          setRawInsights([]);
          return;
        }

        const url = view
          ? `${API_BASE}/api/insights/recent-activity?view=${encodeURIComponent(view)}`
          : `${API_BASE}/api/insights/recent-activity`;

        const response = await httpRequest(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const responseData = await response.json();

        if (responseData && responseData.insights && responseData.insights.length > 0) {
          setRawInsights(responseData.insights);
        } else {
          setRawInsights([]);
        }
      } catch (err) {
        logger.warn("Failed to fetch insights:", err);
        setRawInsights([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [view, variant]);

  const {
    currentIndex,
    goToIndex,
    pause,
    resume,
    handleTouchStart,
    handleTouchEnd,
  } = useInsightCarousel(insights.length, autoRotateInterval, insights.length > 1);

  const handleKeepGoing = useCallback((ctaContext, insight) => {
    pause();

    const { action } = ctaContext;
    const gameData = insight?.data;
    const gameObj = gameData?.game_id
      ? { id: gameData.game_id, game_id: gameData.game_id, name: gameData.game_name }
      : null;

    if (action === 'open_curate') {
      if (gameObj && onShowSimilar) {
        onShowSimilar(gameObj);
      } else if (onOpenCurate) {
        onOpenCurate(ctaContext.message);
      }
    } else if (action === 'add_to_playlist') {
      if (gameData?.game_id) {
        const game = { game_id: gameData.game_id, name: gameData.game_name };
        if (onAddToPlayList) {
          onAddToPlayList(game);
        } else {
          addToPlayList(game);
        }
        showPlayListBanner(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'inherit', color: 'white' }}>
            Added to Play List <List size={16} />
          </span>
        );
      }
    } else if (action === 'letsplay') {
      if (gameObj && onLetsPlay) onLetsPlay(gameObj);
    } else if (action === 'navigate' && ctaContext.destination) {
      navigate(ctaContext.destination);
    } else if (action === 'navigate_with_players' && ctaContext.players) {
      navigate('/quick-decision', { state: { players: ctaContext.players } });
    } else if (action === 'search_sessions' && ctaContext.query) {
      if (onSearchSessions) onSearchSessions(ctaContext.query);
    } else if (action === 'filter_wins') {
      if (onFilterWins) onFilterWins();
    } else if (action === 'collection_to_be_played') {
      if (onCollectionAction) onCollectionAction({ type: 'to_be_played' });
    } else if (action === 'collection_sort') {
      if (onCollectionAction) onCollectionAction({ type: 'sort', sortBy: ctaContext.sortBy, sortOrder: ctaContext.sortOrder });
    } else if (action === 'coming_soon') {
      setComingSoonMessage(ctaContext.message || 'Coming soon');
      return; // keep paused while modal is open
    }

    resume();
  }, [navigate, onOpenCurate, onShowSimilar, onLetsPlay, onSearchSessions, onFilterWins, onAddToPlayList, onCollectionAction, pause, resume, addToPlayList, showPlayListBanner]);

  const handleDismiss = useCallback((insight) => {
    const label = insight?.label;
    if (!label) return;
    saveDismissed({ ...loadDismissed(), [label]: Date.now() });
    setDismissTick((t) => t + 1);
  }, []);

  if (loading) {
    return null;
  }

  const comingSoonModal = (
    <ComingSoonModal
      message={comingSoonMessage}
      onClose={() => { setComingSoonMessage(null); resume(); }}
    />
  );

  if (rawInsights.length > 0 && insights.length === 0) {
    return (
      <>
        {comingSoonModal}
        <div className={`insight-carousel ${className}`} style={{ width: '100%' }}>
          <div
            className="insight-card"
            style={{
              background: 'var(--bg-secondary, #fafbfc)',
              borderRadius: '12px',
              padding: compact ? '12px 16px' : '20px 24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid var(--border-color, #ddd)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{
              color: 'var(--text-secondary, #6b7280)',
              fontSize: compact ? '14px' : '16px',
              lineHeight: 1.4,
            }}>
              All insights dismissed.
            </span>
            <button
              onClick={() => {
                saveDismissed({});
                setDismissTick((t) => t + 1);
              }}
              style={{
                padding: compact ? '5px 12px' : '7px 16px',
                background: 'var(--pista-green-primary, #2E7D32)',
                color: 'var(--pista-button-text, #fff)',
                border: 'none',
                borderRadius: '6px',
                fontSize: compact ? '12px' : '13px',
                fontWeight: 600,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Load Insights
            </button>
          </div>
        </div>
      </>
    );
  }

  if (insights.length === 0) {
    return null;
  }

  return (
    <>
    {comingSoonModal}
    {playListBanner.show && (
      <div
        style={{
          position: "fixed",
          top: "calc(80px + env(safe-area-inset-top, 0px))",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          padding: "12px 20px",
          borderRadius: "8px",
          backgroundColor: 'var(--success-color, #4caf50)',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500',
          animation: "slideInTop 0.3s ease-out",
          maxWidth: '400px',
          wordWrap: 'break-word',
          textAlign: 'center',
        }}
      >
        {playListBanner.message}
      </div>
    )}
    <div
      className={`insight-carousel ${className}`}
      style={{
        position: "relative",
        width: "100%",
      }}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="insight-carousel-container"
        style={{
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          className="insight-carousel-slides"
          style={{
            display: "flex",
            transition: "transform 0.3s ease",
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {insights.map((insight, index) => (
            <div
              key={index}
              className="insight-carousel-slide"
              style={{
                flex: "0 0 100%",
                minWidth: "100%",
                padding: "0 4px",
              }}
            >
              <InsightCard
                insight={insight}
                compact={compact}
                onKeepGoing={handleKeepGoing}
                onDismiss={handleDismiss}
                isActive={index === currentIndex}
              />
            </div>
          ))}
        </div>
      </div>

      {showNavigation && insights.length > 1 && (
        <div
          className="insight-carousel-dots"
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "6px",
            marginTop: "12px",
          }}
        >
          {insights.map((_, index) => (
            <button
              key={index}
              onClick={() => goToIndex(index)}
              className={`insight-carousel-dot ${index === currentIndex ? 'active' : ''}`}
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                border: "none",
                cursor: "pointer",
                transition: "all 0.2s ease",
                background: index === currentIndex
                  ? "var(--pista-green-primary, #2E7D32)"
                  : "var(--pista-grey-40, #BDBDBD)",
              }}
              aria-label={`Go to insight ${index + 1}`}
              aria-current={index === currentIndex ? "true" : "false"}
            />
          ))}
        </div>
      )}

    </div>
    </>
  );
}

export default InsightCarousel;
